// react_frontend/src/Components/Modules/Auth.js
import React, { useState, useContext } from "react";
import axios from "axios";
import Constant from "../../Constant";
import authBg from "../../Assets/Media/authBg.webp";
import { Link, useNavigate } from "react-router-dom";
import {
  FaRegEye, FaRegEyeSlash, FaHome, FaShieldAlt, FaRocket,
  FaChartLine, FaUsers, FaKey, FaCheckCircle, FaTimes,
} from "react-icons/fa";
import { GrLicense } from "react-icons/gr";
import { MotherContext } from "../../Context/MotherContext";


import kora from "../../Assets/Media/lumen-logo.svg";
import koraFav from "../../Assets/Media/lumen-logo-white.svg";

const Auth = () => {
  const { fetchProfile } = useContext(MotherContext);

  const [loginInput, setLoginInput] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [licenseModal, setLicenseModal] = useState(false);
  const [licenseKey, setLicenseKey] = useState("");
  const [pendingEmail, setPendingEmail] = useState(null);
  const [isTrialUsed, setIsTrialUsed] = useState(false);
  const [step, setStep] = useState("choose"); // "choose", "trial", "purchase"

  const navigate = useNavigate();

  const handleLoginInput = (e) => {
    setLoginInput({ ...loginInput, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const res = await axios.post(`${Constant.BASE_URL}/login`, loginInput);
      const data = res.data;

      if (data.license_status === "active") {
        Object.entries({
          id: data.id,
          email: data.email,
          who_am_i: data.who_am_i,
          token: data.token,
          company_id: data.company_id,
          license_status: data.license_status,
          max_users: data.company.max_users,
          package: data.company.package,
          max_products: data.company.max_products,
          has_custom_report: data.company.has_custom_report,
          name: data.name,
        }).forEach(([key, value]) => localStorage.setItem(key, value));

        await fetchProfile();
        const userRole = parseInt(data.who_am_i);
        const redirectPath = userRole === 4 ? "/hr/employee-dashboard" : "/dashboard";
        navigate(redirectPath);
        window.location.replace(redirectPath);
      } else {
        if (data.trial_used) {
          localStorage.setItem("pendingEmail", loginInput.email);
          navigate("/choose-package");
          return;
        }

        setPendingEmail(loginInput.email);
        localStorage.setItem("pendingEmail", loginInput.email);
        setStep("choose");
        setLicenseModal(true);
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 422) {
          setErrors(data.errors || {});
        } else if (status === 403 && data.error) {
          if (data.trial_used) {
            localStorage.setItem("pendingEmail", loginInput.email);
            navigate("/choose-package");
            return;
          }

          setPendingEmail(loginInput.email);
          localStorage.setItem("pendingEmail", loginInput.email);
          setStep("choose");
          setLicenseModal(true);
        } else {
          setErrors({ general: "Something went wrong." });
        }
      } else {
        setErrors({ general: "Network error." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLicenseSubmit = async () => {
    if (!licenseKey || !pendingEmail) return;

    try {
      const res = await axios.post(`${Constant.BASE_URL}/verify-license`, {
        license_key: licenseKey,
        email: pendingEmail,
      });

      const data = res.data;

      if (data.valid) {
        Object.entries({
          id: data.id,
          email: data.email,
          who_am_i: data.who_am_i,
          token: data.token,
          company_id: data.company_id,
          license_status: data.license_status,
          max_users: data.company.max_users,
          package: data.company.package,
          max_products: data.company.max_products,
          has_custom_report: data.company.has_custom_report,
          name: data.name,
        }).forEach(([key, value]) => localStorage.setItem(key, value));

        setLicenseModal(false);
        await fetchProfile();
        const userRole = parseInt(data.who_am_i);
        const redirectPath = userRole === 4 ? "/hr/employee-dashboard" : "/dashboard";
        navigate(redirectPath);
        window.location.replace(redirectPath);
      } else {
        alert(data.msg || "Invalid license key.");
      }
    } catch (err) {
      alert(err.response?.data?.msg || "License verification failed.");
    }
  };

  const handlePurchase = () => {
    navigate("/choose-package");
  };

  return (
    <div className="auth-page">
      {/* ===== Left: Branding panel ===== */}
      <div className="auth-brand" style={{ backgroundImage: `url(${authBg})` }}>
        <div className="auth-brand-overlay" />
        <div className="auth-brand-content">
          <div className="auth-logo-badge">
            <img src={koraFav} alt="Kora Logo" className="auth-logo-img" />
          </div>
          <h1>Lumen</h1>
          <p className="auth-tagline">
            The central hub of your business. Manage sales, inventory, HR, and payroll in one modern platform.
          </p>
          <ul className="auth-features">
            <li><FaChartLine /> Real-time insights & analytics</li>
            <li><FaUsers /> Complete HR & payroll management</li>
            <li><FaRocket /> Fast, secure & scalable</li>
          </ul>
        </div>
      </div>

      {/* ===== Right: Login form ===== */}
      <div className="auth-form-side">
          <img src={kora} alt="Kora Logo" className="auth-logo-img" />
        <div className="auth-card">
          <div className="auth-card-header">
            <h4>Sign in to Lumen</h4>
            <p>Enter your credentials to access your account</p>
          </div>

          {errors.general && <div className="auth-alert danger">{errors.general}</div>}

          <form onSubmit={handleLoginSubmit}>
            <div className="auth-field">
              <label htmlFor="email"><FaHome /> Email Address</label>
              <input
                id="email"
                type="email"
                name="email"
                onChange={handleLoginInput}
                value={loginInput.email}
                placeholder="Enter a valid email address"
                required
              />
              {errors.email && <span className="auth-error">{errors.email[0]}</span>}
            </div>

            <div className="auth-field">
              <label htmlFor="password"><FaShieldAlt /> Password</label>
              <div className="auth-password-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  onChange={handleLoginInput}
                  value={loginInput.password}
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  className="auth-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <FaRegEyeSlash /> : <FaRegEye />}
                </button>
              </div>
              {errors.password && <span className="auth-error">{errors.password[0]}</span>}
            </div>

            <button type="submit" className="auth-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm" /> Signing in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="auth-links">
            <Link to="/forgot-password">Forgot Password?</Link>
            <Link to="/company-register">Register Your Company</Link>
          </div>
        </div>

        <footer className="auth-footer">
          <span>© SSM-Kora {new Date().getFullYear()} | Version 2.0</span>
          <span>
            Powered by{" "}
            <a href="https://www.softsolutionmedia.com" target="_blank" rel="noopener noreferrer">
              Soft Solution Media
            </a>
          </span>
        </footer>
      </div>

      {/* ===== License Modal ===== */}
      {licenseModal && (
        <div className="auth-modal-overlay" onClick={() => setLicenseModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="auth-modal-header">
              <div className="auth-modal-icon">
                <FaKey />
              </div>
              <h5>Activate License</h5>
              <button type="button" className="auth-modal-close" onClick={() => setLicenseModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="auth-modal-body">
              {/* Step 1: Choose Option */}
              {step === "choose" && (
                <>
                  <p className="auth-modal-text">
                    <GrLicense /> Your company license is inactive. Please choose an option to continue.
                  </p>

                  {!isTrialUsed && (
                    <button className="auth-modal-btn success" onClick={() => setStep("trial")}>
                      <FaCheckCircle /> Use Trial License
                    </button>
                  )}

                  {isTrialUsed && (
                    <button className="auth-modal-btn primary" onClick={() => setStep("purchase")}>
                      <FaKey /> Buy 1 Year License
                    </button>
                  )}
                </>
              )}

              {/* Step 2: Enter License Key */}
              {step === "trial" && (
                <>
                  <p className="auth-modal-text">
                    <GrLicense /> Enter your license key to activate:
                  </p>
                  <input
                    type="text"
                    className="auth-modal-input"
                    placeholder="Enter License Key"
                    value={licenseKey}
                    onChange={(e) => setLicenseKey(e.target.value)}
                  />
                  <button className="auth-modal-btn primary" onClick={handleLicenseSubmit}>
                    Verify & Continue
                  </button>
                  <button className="auth-modal-btn secondary" onClick={() => setStep("choose")}>
                    Back
                  </button>
                </>
              )}

              {/* Step 3: Confirm Purchase */}
              {step === "purchase" && (
                <>
                  <p className="auth-modal-text">
                    <GrLicense /> Confirm purchase of 1-year license?
                  </p>
                  <button className="auth-modal-btn primary" onClick={handlePurchase}>
                    Confirm Purchase
                  </button>
                  <button className="auth-modal-btn secondary" onClick={() => setStep("choose")}>
                    Back
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;